import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MdDeleteForever } from "react-icons/md";
import Loader from '../../components/Global/Loader';
import { FaEdit } from 'react-icons/fa';
import { getDisplayValue } from '../../utils/displayValue';
function CalculatorTable() {
    const [calculators, setCalculators] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [calculatorToDelete, setCalculatorToDelete] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        fetchCalculators();
    }, []);

    const fetchCalculators = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/calculator`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });
            // Add console.log to check the response structure
            // console.log('API Response:', response.data.data);

            // Check if response.data is an array, if not, extract the array from the correct pro
            setCalculators(response.data.data);
        } catch (error) {
            // console.error('Error fetching calculator data:', error);
            setError(error.response?.data?.message || 'Failed to fetch calculator data');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (id) => {
        navigate(`/admin/calculator-details?id=${id}`);
    };

    const handleDelete = (calculator) => {
        setCalculatorToDelete(calculator);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        try {
            setLoading(true);
            await axios.delete(
                `${process.env.REACT_APP_BASE_URL}/calculator/${calculatorToDelete._id}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );
            setShowDeleteModal(false);
            setCalculatorToDelete(null);
            fetchCalculators(); // Refresh the list
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to delete calculator');
        } finally {
            setLoading(false);
        }
    };


    // Get currency symbol based on country
    const getCurrencySymbol = (record) => {
        if (record.country === 'UK') {
            return '£';
        } else if (record.country === 'USA') {
            return '$';
        }
        return '';
    };


    const filteredCalculators = calculators.filter((calculator) => {
        const searchTerm = searchQuery.toLowerCase();
        return (
            calculator?.calculatorId?.toLowerCase().includes(searchTerm) ||
            getDisplayValue(calculator?.university, '').toLowerCase().includes(searchTerm) ||
            getDisplayValue(calculator?.country, '').toLowerCase().includes(searchTerm) ||
            getDisplayValue(calculator?.programme, '').toLowerCase().includes(searchTerm) ||
            getDisplayValue(calculator?.city, '').toLowerCase().includes(searchTerm) ||
            getDisplayValue(calculator?.major, '').toLowerCase().includes(searchTerm)
        );
    });

    return (
        <div id="main-content" className="relative h-screen pl-36 mt-16 w-full p-8">
            {loading && <Loader />}

            {/* Add the delete confirmation modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-[30%]">
                        <h2 className="text-lg font-semibold mb-4">Confirm Deletion</h2>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to delete{' '}
                            <span className="font-semibold">
                                {calculatorToDelete?.calId}
                            </span>
                            ? This action cannot be undone.
                        </p>
                        <div className="flex justify-end space-x-4">
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setCalculatorToDelete(null);
                                }}
                                className="px-4 py-2 text-gray-500 border border-gray-300 rounded hover:bg-gray-100"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-semibold text-gray-800">Added Calculations</h1>
                    <div className="flex items-center space-x-4">
                        <div className="relative">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search calculations..."
                                className="py-2 px-3 w-64 text-sm text-gray-600 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-600 transition duration-300 shadow-sm" style={{ borderBottomWidth: "3px", borderBottomColor: "#3b82f6" }}
                            />
                            <i className="fas fa-search absolute right-3 top-3 text-gray-400"></i>
                        </div>
                        <button
                            className="px-4 py-2 font-semibold text-white rounded bg-blue-500 hover:bg-blue-600 flex items-center"
                            onClick={() => navigate('/admin/calculator-details')}
                        >
                            <i className="fas fa-plus mr-2"></i>
                            Add calculator
                        </button>
                    </div>
                </div>
                <table className="w-full table-auto border-t border-b border-gray-300">
                    <thead>
                        <tr className="border-b border-gray-300">
                            <th className="px-4 py-2 text-center text-gray-400">calculation ID</th>
                            <th className="px-4 py-2 text-center text-gray-400">Country</th>
                            <th className="px-4 py-2 text-center text-gray-400">University Name</th>
                            <th className="px-4 py-2 text-center text-gray-400">City/Town</th>
                            <th className="px-4 py-2 text-center text-gray-400">Program</th>
                            <th className="px-4 py-2 text-center text-gray-400">Major</th>
                            <th className="px-4 py-2 text-center text-gray-400">Application Fee</th>
                            <th className="px-4 py-2 text-center text-gray-400">Total Fee</th>

                            <th className="px-4 py-2 text-center text-gray-400">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Array.isArray(filteredCalculators) && filteredCalculators.length > 0 ? (
                            filteredCalculators.map((calculator) => (
                                <tr
                                    key={calculator._id}
                                    className="border-b border-gray-300 cursor-pointer text-center hover:bg-gray-50"
                                >
                                    <td className="px-4 py-4 text-sm text-gray-700">{calculator.calId}</td>
                                    <td className="px-4 py-4 text-sm text-gray-700">{getDisplayValue(calculator.country)}</td>
                                    <td className="px-4 py-4 text-sm text-gray-700">{getDisplayValue(calculator.university)}</td>
                                    <td className="px-4 py-4 text-sm text-gray-700">{getDisplayValue(calculator.city)}</td>
                                    <td className="px-4 py-4 text-sm text-gray-700">{getDisplayValue(calculator.programme)}</td>
                                    <td className="px-4 py-4 text-sm text-gray-700">{getDisplayValue(calculator.major)}</td>
                                    <td className="px-4 py-4 text-sm text-gray-700">
                                        <span className='font-bold'>{getCurrencySymbol(calculator) + " "}</span>
                                        {calculator.applicationFee}
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-700">
                                        <span className='font-bold'>{getCurrencySymbol(calculator) + " "}</span>
                                        {calculator.totalFee}
                                    </td>
                                    <td className="px-4 pt-4 flex items-center justify-center space-x-3">
                                        <FaEdit
                                            className="text-blue-500 mr-1 cursor-pointer hover:text-blue-600"
                                            onClick={() => handleEdit(calculator._id)}
                                            size={20}
                                        />
                                        <MdDeleteForever
                                            className="text-red-500 cursor-pointer hover:text-red-600"
                                            onClick={() => handleDelete(calculator)}
                                            size={22}
                                        />
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                                    <div className="flex flex-col items-center justify-center">
                                        <i className="fas fa-search text-4xl mb-3 text-gray-300"></i>
                                        <p className="text-lg">
                                            {searchQuery
                                                ? "No calculations found matching your search criteria"
                                                : "No calculations available"}
                                        </p>
                                        <p className=" text-gray-400 mt-1">
                                            {searchQuery
                                                ? "Try adjusting your search terms"
                                                : "Calculations will appear here once added"}
                                        </p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {error && (
                    <div className="text-center text-red-500 mt-4">
                        {error}
                    </div>
                )}

                {!loading && (!calculators || calculators.length === 0) && (
                    <div className="text-center text-gray-500 mt-4">
                        No calculations found
                    </div>
                )}
            </div>
        </div>
    );
}

export default CalculatorTable;
